import { Fragment, useEffect, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import Api from "../../api/api"
import { Sku } from "../../api/models"
import { useSession } from "../../auth/session"
import { LOGIN_URL } from "../../auth/amplify"
import { useSignOut } from "../../auth/signOut"
import { claimedOrdersSelector, dismissClaimedOrders, logIn, meSelector } from "../../redux/globalSlice"
import SkuImage from "../widgets/SkuImage"

// the light wordmark the login app shows above its forms — the page is a
// sibling of those screens in the buyer's journey
const LOGO_URL = "https://downloads.newfangledaudio.com/nfa/images/logo-long-light.png"

const POLL_INTERVAL_MS = 3000
const POLL_TIMEOUT_MS = 60 * 1000

// LemonSqueezy redirects a GUEST checkout (bought with no account) here,
// with ?skuId=<uuid> for the product picture. The page is PUBLIC — it renders
// outside App's session gate and the app shell — because the buyer may be
// signed out, or signed in as somebody else entirely on this machine, and
// either way must not be bounced around without a word. What it can say
// depends on the session:
// - signed out: the purchase is parked for the email entered at checkout
//   (guest_orders) and waits for an account with that address — point at
//   the login app's sign-up / sign-in, which carry the same instruction.
// - signed in: the bootstrap /me (App runs it for any signed-in visitor)
//   is what materializes a parked purchase whose email matches this
//   account, and reports it as claimedOrders. The LS redirect usually
//   lands BEFORE the webhook has parked the order, though, so the page
//   keeps re-fetching /me for a minute — each call is one more chance to
//   materialize — before concluding the purchase belongs to another
//   address and offering a sign-out that returns to the login app with
//   the purchase notice.
export type GuestPurchaseBootstrap = { status: "idle" | "loading" | "ready" | "error", message?: string }

// The address the buyer checked out with, when the redirect carries it:
// the guest checkout's redirect URL ends in `email=[email]`, a Lemon Squeezy
// link variable LS replaces with the real address when it builds the
// confirmation button. A hint only — it prefills the login app's forms
// (materialization still needs a VERIFIED account with that address), so
// anything that isn't plainly an address is dropped: the literal
// placeholder if LS ever stops substituting, or a hand-edited value. A "+"
// that arrived unencoded reads as a space; addresses have none, so put it
// back.
function buyerEmail(raw: string | null): string | null {
  if (raw === null)
    return null
  const email = raw.trim().replace(/ /g, "+")
  return /^[^\s@[\]<>"']+@[^\s@[\]<>"']+\.[^\s@[\]<>"']+$/.test(email) && email.length <= 320 ? email : null
}

export default function GuestPurchasePage({ session, bootstrap }: {
  session: { isLoading: boolean, isAuthenticated: boolean }
  bootstrap: GuestPurchaseBootstrap
}) {
  const [searchParams] = useSearchParams()
  const skuId = searchParams.get("skuId")
  const email = buyerEmail(searchParams.get("email"))
  const [sku, setSku] = useState<Sku | null>(null)

  // the public sku read — no token, so it works signed out
  useEffect(() => {
    if (skuId === null) return
    let cancelled = false
    Api.getSku({ skuId })
      .then((r) => { if (!cancelled) setSku(r.sku) })
      .catch((err) => console.error("Could not load the purchased sku:", err))
    return () => { cancelled = true }
  }, [skuId])

  return (
    <div className="guest-purchase-shell">
      <img src={LOGO_URL} alt="Newfangled Audio" className="guest-purchase-logo" width={3000} height={301} />
      <div className="page">
        <div className="dashboard-header">
          <h1 className="page-title" style={{ margin: 0 }}>Thank you for your purchase!</h1>
        </div>
        {sku !== null && <SkuImage sku={sku} alt={sku.name} />}
        {session.isLoading ? (
          <p className="empty-state"><span className="spinner" /> Finalizing your order…</p>
        ) : session.isAuthenticated && bootstrap.status !== "error" ? (
          <SignedInOutcome bootstrap={bootstrap} email={email} />
        ) : (
          <SignedOutCard email={email} errorMessage={bootstrap.status === "error" ? bootstrap.message : undefined} />
        )}
      </div>
    </div>
  )
}

// The login app's two doors, both returning to the Licenses page — which
// is where the purchase shows up once the right account is signed in. The
// checkout address, when known, prefills either form (login_hint).
function loginAppUrl(screen: "login" | "signup", email: string | null): string {
  const params = new URLSearchParams({ returnTo: `${window.location.origin}/licenses`, notice: "purchase" })
  if (email !== null)
    params.set("login_hint", email)
  return `${LOGIN_URL}/${screen}?${params.toString()}`
}

function SignedOutCard({ email, errorMessage }: { email: string | null, errorMessage?: string }) {
  return (
    <div className="card" style={{ marginTop: 24 }}>
      <div className="card-title">Your purchase is waiting for you</div>
      <div className="card-detail">
        We've sent an email to {email !== null ? <strong>{email}</strong> : "the address you entered at checkout"} with
        everything you need. To get your licenses, create a free Newfangled Audio account — or sign in if you already
        have one — <strong>using that same email address</strong>. Your purchase will be waiting for you.
      </div>
      {errorMessage && (
        <div className="card-detail" style={{ opacity: 0.7 }}>We couldn't load your account right now ({errorMessage}) — the steps above work regardless.</div>
      )}
      <div className="card-links">
        <a className="btn-upgrade" href={loginAppUrl("signup", email)}>Create an account</a>
        <a className="card-link" href={loginAppUrl("login", email)}>Sign in</a>
      </div>
    </div>
  )
}

function SignedInOutcome({ bootstrap, email }: { bootstrap: GuestPurchaseBootstrap, email: string | null }) {
  const { accessToken } = useSession()
  const me = useSelector(meSelector)?.data
  const claimed = useSelector(claimedOrdersSelector)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const signOut = useSignOut()
  const [timedOut, setTimedOut] = useState(false)
  const [pollGeneration, setPollGeneration] = useState(0)
  const confirmed = claimed.length > 0
  const bootstrapped = bootstrap.status === "ready"

  // the bootstrap /me ran once; keep asking until the purchase lands on
  // this account or the window runs out
  useEffect(() => {
    if (!bootstrapped || confirmed || !accessToken)
      return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const startedAt = Date.now()
    setTimedOut(false)
    const poll = async () => {
      if (cancelled) return
      try {
        const me = await Api.getMe(accessToken)
        if (cancelled) return
        if (me.claimedOrders.length > 0) {
          dispatch(logIn({ user: me.user, claimedOrders: me.claimedOrders }))
          return
        }
      } catch (err) {
        // transient errors right after the redirect — keep polling
        console.error("Account poll failed:", err)
      }
      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        setTimedOut(true)
        return
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS)
    }
    timer = setTimeout(poll, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      if (timer !== undefined) clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapped, confirmed, accessToken, pollGeneration])

  if (confirmed) {
    const names = claimed.map((o, i) => (
      <Fragment key={o.orderId}>
        {i > 0 && (i === claimed.length - 1 ? " and " : ", ")}
        <strong>{o.skuName}</strong>
      </Fragment>
    ))
    return (
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-title">Added to your account</div>
        <div className="card-detail">
          {names} {claimed.length === 1 ? "has" : "have"} been added to your account. Your licenses are ready to use.
        </div>
        <div className="card-links">
          {/* the banner would only repeat what this page just said */}
          <button className="btn-upgrade" onClick={() => { dispatch(dismissClaimedOrders()); navigate("/licenses") }}>
            View licenses
          </button>
        </div>
      </div>
    )
  }

  if (!bootstrapped)
    return <p className="empty-state"><span className="spinner" /> Finalizing your order…</p>

  // the card shows at once — the check runs visibly inside it, so a buyer
  // whose purchase belongs to another address is never left staring at a
  // spinner for the whole window
  return (
    <div className="card" style={{ marginTop: 24 }}>
      <div className="card-title">Payment received</div>
      <div className="card-detail">
        You're signed in as <strong>{me?.id ?? "another account"}</strong>. If that account uses the email
        address you entered at checkout{email !== null && <> (<strong>{email}</strong>)</>}, your purchase will
        appear here in a moment. If it uses a different address, the purchase is waiting for an account with
        the checkout address — sign out and sign in (or sign up) with it. The email we sent you has the details.
      </div>
      <div className="card-detail" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {timedOut
          ? <>This account hasn't received the purchase yet.{" "}
              <button
                className="card-link"
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                onClick={() => setPollGeneration(pollGeneration + 1)}
              >
                Keep checking
              </button></>
          : <><span className="spinner" /> Checking whether it has landed on this account…</>}
      </div>
      <div className="card-links">
        <button className="btn-upgrade" onClick={() => { void signOut({ landing: loginAppUrl("login", email) }) }}>
          Sign out and use the other address
        </button>
        <Link to="/licenses" className="card-link">Go to Licenses</Link>
      </div>
    </div>
  )
}
